import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { DataWindow, useMetaData } from "../../api/useMetaDataApi";
import {
  usePayerLandscapeComparison,
  usePayerLandscapeComparisonFetchExport,
  usePayerLandscapeComparisonProducts,
} from "../../api/usePayerLandscapeComparisonApi";
import Table from "../../components/Table/Table";
import {
  useClientConfiguration,
  useClientPayerLandscapeComparisonConfig,
} from "../../data/global/useProductConfiguration";
import { usePayerLandscapeDispatch, usePayerLandscapeState } from "../../data/payerLandscape/hooks";
import { SortDirection } from "../../generate";
import useGetRole, { BIOSIM_ROLE, FRM_ROLE } from "../../hooks/useGetRole";
import PrintWinLegend from "./PrintWinLegend";

// Define the sort type based on what's in your actual code
interface Sort {
  field: string;
  direction: SortDirection;
}

const PayerLandscapeComparisonTab: React.FC = () => {
  const { comparisonSort } = usePayerLandscapeState();
  const { updateComparisonSort } = usePayerLandscapeDispatch();
  const { client: selectedClient } = useParams() as { client: string };
  const { t, i18n } = useTranslation();

  // Fetch data with memoized dependencies
  const roleData = useGetRole(selectedClient);
  const role = roleData.data;
  const isFRM = useMemo(() => String(role) === FRM_ROLE, [role]);
  const isBiosim = useMemo(() => String(role) === BIOSIM_ROLE, [role]);

  // Memoize config data
  const clientConfig = useClientConfiguration(selectedClient);
  const allowedPlanInfoProducts = clientConfig.products;

  const comparisonConfig = useClientPayerLandscapeComparisonConfig(selectedClient);

  // Fixed value, no need to recalculate
  const timePeriod = DataWindow.twelveMonths;

  // Memoize API calls parameters
  const productsParams = useMemo(
    () => ({
      client: selectedClient,
    }),
    [selectedClient],
  );

  const { isLoading: isLoadingProducts, data: products } = usePayerLandscapeComparisonProducts(productsParams.client);

  const compareParams = useMemo(
    () => ({
      client: selectedClient,
      shouldFetch: !isLoadingProducts,
    }),
    [selectedClient, isLoadingProducts],
  );

  const compareQuery = usePayerLandscapeComparison(compareParams.client, compareParams.shouldFetch);

  const metaDataParams = useMemo(
    () => ({
      client: selectedClient,
      timePeriod: timePeriod || "",
    }),
    [selectedClient, timePeriod],
  );

  const { data: metaData } = useMetaData(metaDataParams.client, metaDataParams.timePeriod);

  const allColumns = useMemo(() => {
    const PlanIndicator = "planIndicator" as const;

    // Base columns that can be configured
    const baseColumns = [
      {
        label: "payerLandscape.planName",
        align: "left",
        dataKey: "m_plan_name",
        component: PlanIndicator,
      },
      {
        label: "payerLandscape.lives",
        align: "right",
        width: 160,
        dataKey: "lives",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: timePeriod
          ? i18n.exists(`payerLandscape.marketTrx.${timePeriod}.${selectedClient}`)
            ? `payerLandscape.marketTrx.${timePeriod}.${selectedClient}`
            : `payerLandscape.marketTrx.${timePeriod}.default`
          : "Loading...",
        align: "right",
        dataKey: "mkt_trx",
        width: 160,
        format: {
          thousandSeparator: true,
        },
      },
    ];

    // Product columns with potential overrides
    const productColumns = (products || [])
      .filter((product) => allowedPlanInfoProducts?.includes(product.name || ""))
      .map(({ key, name }) => {
        // Check for client-specific overrides for this product
        const override = comparisonConfig.productColumnOverrides?.[`${key}_status`];

        return {
          label: override?.label || (name || "").toUpperCase(),
          subLabel:
            override?.subLabel ||
            (i18n.exists(`payerLandscape.compTabCoverageStatus.${selectedClient}`)
              ? `payerLandscape.compTabCoverageStatus.${selectedClient}`
              : `payerLandscape.compTabCoverageStatus.default`),
          align: override?.align || "left",
          width: override?.width || 160,
          dataKey: `${key}_status`,
        };
      });

    // Create a map of all columns by dataKey for easy lookup
    const columnMap = new Map();

    [...baseColumns, ...productColumns].forEach((column) => {
      columnMap.set(column.dataKey, column);
    });

    return columnMap;
  }, [products, allowedPlanInfoProducts, selectedClient, comparisonConfig, timePeriod, i18n]);

  // Create sorted columns based on dataKeys configuration
  const sortedColumns = useMemo(() => {
    const { dataKeys } = comparisonConfig;

    // If dataKeys is empty or undefined, return all columns
    if (!dataKeys || dataKeys.length === 0) {
      return Array.from(allColumns.values());
    }

    // Get all columns in the exact order specified by dataKeys
    const orderedColumns = dataKeys.map((dataKey) => allColumns.get(dataKey)).filter(Boolean);

    return orderedColumns;
  }, [comparisonConfig, allColumns]);

  // Filter columns based on role (similar to DetailTab logic)
  const roleColumns = useMemo(() => {
    const frmExcluded = ["mkt_trx"];

    return isFRM ? sortedColumns.filter((column) => !frmExcluded.includes(column.dataKey)) : sortedColumns;
  }, [isFRM, sortedColumns]);

  const columnNames = useMemo(
    () => roleColumns.map((column) => t(column.label, column.translationParams) as string),
    [roleColumns, t],
  );

  const columnKeys = useMemo(() => roleColumns.map((column) => column.dataKey), [roleColumns]);

  const exportParams = useMemo(
    () => ({
      client: selectedClient,
      columnKeys,
      columnNames,
    }),
    [selectedClient, columnKeys, columnNames],
  );

  const exportTable = usePayerLandscapeComparisonFetchExport(
    exportParams.client,
    exportParams.columnKeys,
    exportParams.columnNames,
  );

  // Memoize sort handler to prevent recreation on each render - with explicit type
  const handleSort = useCallback(
    (sort: Sort) => {
      updateComparisonSort(sort);
    },
    [updateComparisonSort],
  );

  // Generate footnote content only once
  const footnoteContent = useMemo(
    () => t(`payerLandscape.productComparisonFootnote.${selectedClient}`),
    [t, selectedClient],
  );

  // Determine export based on role
  const finalExportTable = useMemo(() => (!isBiosim ? exportTable : undefined), [isBiosim, exportTable]);

  return (
    <>
      <Table
        columns={roleColumns}
        query={compareQuery}
        sort={comparisonSort}
        handleSort={handleSort}
        stickyMargin={54}
        legend={<PrintWinLegend />}
        footnote={footnoteContent}
        boldFootnote={selectedClient === "CIBINQO"}
        metaData={metaData}
        export={finalExportTable}
        role={String(role)}
      />
    </>
  );
};

export default React.memo(PayerLandscapeComparisonTab);
